import React from 'react';
import { Package, Shield, Clock, Award, Settings, GitCompare } from 'lucide-react';
import './ChatComponents.css';

/**
 * Grid of product cards with coverage info
 */
function ProductCards({ title, data, actions, onAction }) {
  const getCoverageColor = (level) => {
    switch (level) {
      case 'full': return 'var(--color-success)';
      case 'partial': return 'var(--color-warning)';
      case 'basic': return 'var(--color-text-muted)';
      default: return 'var(--color-border)';
    }
  };

  const getComplexityLabel = (complexity) => {
    switch (complexity) {
      case 'low': return { label: 'Easy setup', color: 'var(--color-success)' };
      case 'medium': return { label: 'Moderate', color: 'var(--color-warning)' };
      case 'high': return { label: 'Complex', color: 'var(--color-error)' };
      case 'expert': return { label: 'Expert required', color: 'var(--color-error)' };
      default: return { label: complexity, color: 'var(--color-text-muted)' };
    }
  };

  const handleCardClick = (product) => {
    if (actions?.find(a => a.type === 'card_click') && onAction) {
      const action = actions.find(a => a.type === 'card_click');
      const query = action.queryTemplate
        .replace('{name}', product.name)
        .replace('{productId}', product.productId)
        .replace('{capability}', data.capabilityName || 'this capability');
      onAction({ type: 'query', query });
    }
  };

  const handleCompare = (product, e) => {
    e.stopPropagation();
    if (onAction) {
      onAction({
        type: 'query',
        query: `Compare ${product.name || product.productId} with competitors`,
      });
    }
  };

  return (
    <div className="cc-product-cards">
      {title && <div className="cc-section-title">{title}</div>}

      <div className="cc-cards-grid">
        {data.products?.map((product, idx) => {
          const complexity = getComplexityLabel(product.setupComplexity);

          return (
            <div
              key={idx}
              className="cc-product-card"
              onClick={() => handleCardClick(product)}
            >
              {/* Header */}
              <div className="cc-card-header">
                <Package size={16} color="var(--color-primary)" />
                <span className="cc-card-name">{product.name || product.productId}</span>
                {product.pillar && (
                  <span className="cc-card-pillar">{product.pillar}</span>
                )}
              </div>

              {/* Coverage badge */}
              <div className="cc-card-coverage">
                <span
                  className="cc-coverage-badge"
                  style={{ backgroundColor: getCoverageColor(product.coverageLevel) }}
                >
                  {product.coverageLevel} coverage
                </span>
              </div>

              {/* Details */}
              <div className="cc-card-details">
                {product.licenseTier && (
                  <div className="cc-card-detail">
                    <Award size={12} />
                    <span>{product.licenseTier} license</span>
                    {product.isAddon && <span className="cc-addon-badge">Add-on</span>}
                  </div>
                )}

                {product.setupComplexity && (
                  <div className="cc-card-detail">
                    <Settings size={12} />
                    <span style={{ color: complexity.color }}>{complexity.label}</span>
                    {product.setupTimeHours && (
                      <span className="cc-time-badge">~{product.setupTimeHours}h</span>
                    )}
                  </div>
                )}
              </div>

              {/* Notes */}
              {product.coverageNotes && (
                <div className="cc-card-notes">{product.coverageNotes}</div>
              )}

              {/* Actions */}
              <div className="cc-card-actions">
                <button
                  className="cc-card-action"
                  onClick={(e) => handleCompare(product, e)}
                  title="Compare with competitors"
                >
                  <GitCompare size={14} />
                  Compare
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ProductCards;
